import * as go from 'gojs';
import * as React from 'react';
import { isEqual } from 'vet-bones/utils';

import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import {
  Diagram,
  DiagramSnapshot,
} from 'src/containers/ProjectDiagram/types/diagram';
import { diagramSnapshotSelector } from 'src/selectors/diagramSnapshot';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

/**
 * Watches for diagram-model changes that arrive in Redux without a new
 * diagram.id (collaboration updates and cross-tab state-sync updates) and
 * imperatively merges them into the live GoJS diagram.
 *
 * When the reducer preserves the local `diagram.id` on a
 * `RECEIVED_COLLABORATION_UPDATE` or a `$isSync` `UPDATED_PROJECT_DIAGRAM`,
 * GoJS does not rebuild. This hook applies the updated model via
 * `mergeNodeDataArray` / `mergeLinkDataArray`, smoothly reflecting remote
 * changes without re-running the initial layout. The wrapping transaction
 * uses `DiagramTransaction.COLLABORATION_SYNC` so the ModelChanged listener
 * in `useDiagramValue` does not re-dispatch the update (which would loop).
 */
export const useCollaborationDiagramSync = (diagram: Diagram | undefined) => {
  const { projectId } = useRouterParams();
  const diagramSnapshot = useReduxSelector(
    diagramSnapshotSelector(projectId),
    isEqual
  );

  const prevSnapshotRef = React.useRef<DiagramSnapshot | null>(null);

  React.useEffect(() => {
    const prevSnapshot = prevSnapshotRef.current;
    prevSnapshotRef.current = diagramSnapshot;

    if (!diagram || !diagramSnapshot || !prevSnapshot) {
      return;
    }

    // Only act when model data changed but the diagram ID stayed the same —
    // a new id means this was a local GoJS change and GoJS will rebuild.
    if (diagramSnapshot.id !== prevSnapshot.id) {
      return;
    }

    const prevModel = prevSnapshot.model;
    const newModel = diagramSnapshot.model;

    const didModelDataChange = !isEqual(
      prevModel.modelData,
      newModel.modelData
    );
    const didNodeDataArrayChange = !isEqual(
      prevModel.nodeDataArray,
      newModel.nodeDataArray
    );
    const didLinkDataArrayChange = !isEqual(
      prevModel.linkDataArray,
      newModel.linkDataArray
    );

    if (
      !didModelDataChange &&
      !didNodeDataArrayChange &&
      !didLinkDataArrayChange
    ) {
      return;
    }

    // Apply updates imperatively through GoJS's merge API
    // eslint-disable-next-line no-param-reassign
    diagram.skipsUndoManager = true;
    diagram.startTransaction(DiagramTransaction.COLLABORATION_SYNC);

    try {
      if (didModelDataChange) {
        const modelData = newModel.modelData as unknown as Record<
          string,
          unknown
        >;
        const currentModelData = diagram.model.modelData as Record<
          string,
          unknown
        >;

        // Set or update keys present in the new model data
        Object.keys(modelData).forEach((key) => {
          if (key !== '__gohashid') {
            diagram.model.set(diagram.model.modelData, key, modelData[key]);
          }
        });

        // Remove keys that existed in GoJS but were removed remotely
        // (e.g., draftData cleared when a draft is accepted/rejected)
        Object.keys(currentModelData).forEach((key) => {
          if (key !== '__gohashid' && !(key in modelData)) {
            diagram.model.set(diagram.model.modelData, key, undefined);
          }
        });
      }

      if (didNodeDataArrayChange) {
        (diagram.model as go.GraphLinksModel).mergeNodeDataArray(
          newModel.nodeDataArray
        );
      }

      if (didLinkDataArrayChange) {
        (diagram.model as go.GraphLinksModel).mergeLinkDataArray(
          newModel.linkDataArray
        );
      }

      // Force bindings to update
      diagram.updateAllTargetBindings();
    } finally {
      diagram.commitTransaction(DiagramTransaction.COLLABORATION_SYNC);
      // eslint-disable-next-line no-param-reassign
      diagram.skipsUndoManager = false;
    }
  }, [diagram, diagramSnapshot]);
};
